import { useCallback, useEffect, useState } from 'react';
import { providerApi } from '../../../../services/api';

export const NOTE_MAX = 2000;

/** One order's private notes: load, add, edit, delete. Re-reads after every mutation (no optimistic state). */
export default function useOrderNotes(orderId, onChanged) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  const load = useCallback(async () => {
    if (!orderId) return;
    setLoading(true);
    setLoadError(false);
    try {
      setNotes(await providerApi.getOrderNotes(orderId) || []);
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => { load(); }, [load]);

  const mutate = async (call, errorKey) => {
    setSaving(true);
    setSaveError('');
    try {
      await call();
      await load();
      onChanged?.();
      return true;
    } catch (err) {
      setSaveError(err.response?.data?.message || errorKey);
      return false;
    } finally {
      setSaving(false);
    }
  };

  return {
    notes, loading, loadError, saving, saveError, reload: load,
    add: (body) => mutate(() => providerApi.addOrderNote(orderId, body), 'saveFailed'),
    edit: (noteId, body) => mutate(() => providerApi.updateOrderNote(orderId, noteId, body), 'saveFailed'),
    remove: (noteId) => mutate(() => providerApi.deleteOrderNote(orderId, noteId), 'deleteFailed'),
  };
}
